import React, { Fragment, useCallback, useState } from 'react';
import cl from 'classnames';
import filesize from 'filesize';

import { FileUrl } from '../../../../../types';

import { S3MultipartUploadOnFileUploaded } from '../../../../../main/s3Multipart/hooks/useS3MultipartUpload';

import { Translate } from '../../../../Translate';

import { DropzoneHelper } from '../../../../dropzone/DropzoneHelper';

import { DropzoneFileUrlFieldControlRequiredProps } from './DropzoneFileUrlFieldControl.types';

type DropzoneFileUrlFieldControlProps = {
  onChange: (fileUrl: FileUrl | null) => void;
};

function DropzoneFileUrlFieldControl({
  accept,
  disabled,
  error,
  errorClassName,
  onChange,
  maxSize,
  type
}: DropzoneFileUrlFieldControlProps &
  DropzoneFileUrlFieldControlRequiredProps) {
  const [dropRejectedError, setDropRejectedError] = useState<string>(null);

  const clearDropRejectedError = useCallback(
    () => setDropRejectedError(null),
    []
  );

  const handleFileUploaded = useCallback<S3MultipartUploadOnFileUploaded>(
    (id, uploadId, fileUrl) => {
      clearDropRejectedError();
      onChange(fileUrl);
    },
    [clearDropRejectedError, onChange]
  );

  const handleRemoveFile = useCallback<() => void>(() => {
    onChange(null);
  }, [onChange]);

  const onDropRejected = useCallback(
    (fileRejections) => {
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((err) => {
          if (err.code === 'file-too-large' && !!maxSize) {
            setDropRejectedError(
              `File "${file.name}" is too big ${filesize(
                file.size
              )} (max ${filesize(maxSize)})`
            );
          }
        });
      });
    },
    [maxSize]
  );

  return (
    <Fragment>
      <DropzoneHelper
        accept={accept}
        disabled={disabled}
        maxFiles={1}
        maxSize={maxSize}
        onDropRejected={onDropRejected}
        onFilesAccepted={clearDropRejectedError}
        onFileUploaded={handleFileUploaded}
        onRemoveFile={handleRemoveFile}
        preventMaxFilesOverload
        type={type}
        withoutTabs
      />

      {(error || dropRejectedError) && (
        <p className={cl(errorClassName || 'mt-2 text-sm text-red-600')}>
          {/^forms\.errors+/.test(error) ? (
            <Translate id={error} />
          ) : (
            error || dropRejectedError
          )}
        </p>
      )}
    </Fragment>
  );
}

export default DropzoneFileUrlFieldControl;
